import { useEffect, useState } from "react";
import { X } from "lucide-react";
import {
  hasCookieConsentDecision,
  saveCookieConsent,
} from "../utils/cookieConsent";

/**
 * Bottom banner for web cookie / local-storage preferences.
 * Necessary storage (auth tokens, language, session audit id) always allowed.
 * Optional analytics gated until the user accepts.
 */
const CookieConsentBanner = () => {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [analytics, setAnalytics] = useState(false);

  useEffect(() => {
    setVisible(!hasCookieConsentDecision());
  }, []);

  if (!visible) return null;

  const acceptAll = () => {
    saveCookieConsent({ analytics: true });
    setVisible(false);
  };

  const acceptNecessary = () => {
    saveCookieConsent({ analytics: false });
    setVisible(false);
  };

  const saveCustom = () => {
    saveCookieConsent({ analytics });
    setVisible(false);
  };

  /** Dismiss via X — keep necessary storage only (no optional analytics). */
  const closeBanner = () => {
    saveCookieConsent({ analytics: false });
    setVisible(false);
  };

  return (
    <div
      role="dialog"
      aria-label="Cookie and storage preferences"
      className="fixed inset-x-0 bottom-0 z-[100] p-4 sm:p-6 pointer-events-none"
    >
      <div className="pointer-events-auto mx-auto max-w-3xl rounded-xl border border-gray-200 bg-white shadow-xl relative">
        <button
          type="button"
          onClick={closeBanner}
          aria-label="Close cookie banner"
          title="Close"
          className="absolute top-3 end-3 p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
        >
          <X size={18} aria-hidden="true" />
        </button>
        <div className="p-5 sm:p-6 pe-12">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">
            Cookies &amp; local storage
          </h2>
          <p className="text-sm text-gray-600 leading-relaxed mb-4">
            We use necessary browser storage to keep you signed in, remember language,
            and support secure sessions. Optional analytics help us understand site
            performance. See our{" "}
            <a href="https://vitra-health.com/privacy-policy" className="text-teal-700 underline font-medium">
              Privacy Policy
            </a>{" "}
            for details. Clinical health-data consent is managed separately in the app.
          </p>

          {showDetails && (
            <div className="mb-4 space-y-3 text-sm border border-gray-100 rounded-lg p-3 bg-gray-50">
              <label className="flex items-start gap-3">
                <input type="checkbox" checked disabled className="mt-1" />
                <span>
                  <span className="font-medium text-gray-900">Necessary</span>
                  <span className="block text-gray-600">
                    Authentication tokens, language preference, client session id for
                    audit correlation, push endpoint binding. Always on.
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={analytics}
                  onChange={(e) => setAnalytics(e.target.checked)}
                />
                <span>
                  <span className="font-medium text-gray-900">Analytics</span>
                  <span className="block text-gray-600">
                    Optional web vitals / performance measurement. Not used for advertising.
                  </span>
                </span>
              </label>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-end">
            <button
              type="button"
              className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900 order-last sm:order-first sm:me-auto"
              onClick={() => setShowDetails((v) => !v)}
            >
              {showDetails ? "Hide options" : "Customize"}
            </button>
            {showDetails ? (
              <button
                type="button"
                onClick={saveCustom}
                className="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50"
              >
                Save preferences
              </button>
            ) : (
              <button
                type="button"
                onClick={acceptNecessary}
                className="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50"
              >
                Necessary only
              </button>
            )}
            <button
              type="button"
              onClick={acceptAll}
              className="px-4 py-2 text-sm rounded-lg bg-teal-600 text-white hover:bg-teal-700 font-medium"
            >
              Accept all
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsentBanner;
